


import React, { useState, useEffect } from "react";
import { database, auth } from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import ReactDOMServer from 'react-dom/server';

// Helper: Extract city from address
// Helper: Extract city from address
const getCityFromAddress = (address) => {
  if (!address) return 'Unknown City';
  const addressParts = address.trim().split(/\s+/);
  return addressParts.length > 0 ? addressParts[addressParts.length - 1] : 'Unknown City';
};

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) { // "All" option
      if (selected.includes(option)) {
        onChange([]);
      } else {
        onChange([...options]);
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter(item => item !== option && item !== options[0])
        : [...selected.filter(item => item !== options[0]), option];
      
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected]);
      } else {
        onChange(newSelected);
      }
    }
  };

  return (
    <div style={{
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      maxWidth: '220px',
      marginBottom: '16px', // Added margin to create space between sections
    }}>
      <h2 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '8px',
        borderBottom: '2px solid #3b82f6',
        paddingBottom: '6px',
      }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map((option) => (
          <label key={option} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            transition: 'background-color 0.2s',
            backgroundColor: selected.includes(option) ? '#eff6ff' : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }
          }}
          onMouseLeave={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: '14px',
                width: '14px',
                accentColor: '#3b82f6',
                cursor: 'pointer',
              }}
            />
            <span style={{ 
              fontSize: '14px',
              color: '#475569',
              userSelect: 'none',
              fontWeight: selected.includes(option) ? '500' : '400',
            }}>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// Purchase Return Report Display Component
const PurchaseReturnReportDisplay = ({ formData, filters, branchProfile, purchaseReturnData }) => {
  // Helper function to parse date
  const parseReturnDate = (dateString) => {
    if (!dateString) return null;
    // Handle DD-MM-YYYY or DD/MM/YYYY
    if (/^\d{2}[-\/]\d{2}[-\/]\d{4}$/.test(dateString)) {
      const parts = dateString.split(/[-\/]/);
      return new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
    }
    // Handle YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const parts = dateString.split('-');
      return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    }
    // Fallback to default Date parsing
    return new Date(dateString);
  };

  // Format date to DD/MM/YYYY for display
  const formatDisplayDate = (dateString) => {
    const date = parseReturnDate(dateString);
    return !date || isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Ensure purchaseReturnData is always an array
  const safeData = Array.isArray(purchaseReturnData) ? purchaseReturnData : [];

  // Apply filters
  let filteredPurchaseReturnData = safeData;

  // Apply date filter for datewise reports
  if (formData.searchBy === "datewise") {
    const fromDate = new Date(formData.fromDate);
    const toDate = new Date(formData.toDate);
    toDate.setHours(23, 59, 59, 999);
    filteredPurchaseReturnData = filteredPurchaseReturnData.filter(item => {
      const itemDate = parseReturnDate(item.returnDate);
      return itemDate && !isNaN(itemDate.getTime()) && itemDate >= fromDate && itemDate <= toDate;
    });
  }

  // Apply supplier filter
  if (!filters.supplier.includes("All Supplier Names")) {
    filteredPurchaseReturnData = filteredPurchaseReturnData.filter(item => 
      filters.supplier.includes(item.supplier || "Unknown Supplier")
    );
  }

  // Apply group filter
  if (!filters.group.includes("All Group Names")) {
    filteredPurchaseReturnData = filteredPurchaseReturnData.filter(item => 
      filters.group.includes(item.groupName || "No Group Assigned")
    );
  }

  // Apply product filter
  if (!filters.product.includes("All Product Names")) {
    filteredPurchaseReturnData = filteredPurchaseReturnData.filter(item => 
      filters.product.includes(item.itemDescription || "Unknown Product")
    );
  }

  // Initialize displayData
  let displayData = [];

  // Process data based on search criteria and report type
  if ((formData.searchBy === "datewise" || formData.searchBy === "supplierwise" || formData.searchBy === "groupwise") && formData.reportType === "abstract") {
    const groupedData = filteredPurchaseReturnData.reduce((acc, item) => {
      const key = formData.searchBy === "datewise" ? item.returnDate : 
                  formData.searchBy === "supplierwise" ? item.supplier : 
                  item.groupName;
      if (!acc[key]) {
        acc[key] = {
          key: key,
          returnNos: [],
          qty: 0,
          value: 0,
          discAmt: 0,
          cgstAmt: 0,
          sgstAmt: 0,
          netValue: 0,
          count: 0
        };
      }
      if (!acc[key].returnNos.includes(item.returnNo) && item.returnNo !== "N/A") {
        acc[key].returnNos.push(item.returnNo);
      }
      acc[key].qty += parseFloat(item.returnQty) || 0;
      acc[key].value += parseFloat(item.value) || 0;
      acc[key].discAmt += parseFloat(item.disc) || 0;
      acc[key].cgstAmt += parseFloat(item.camt) || 0;
      acc[key].sgstAmt += parseFloat(item.samt) || 0;
      acc[key].netValue += parseFloat(item.valueWithTax) || 0;
      acc[key].count += 1;
      return acc;
    }, {});
    displayData = Object.values(groupedData);
    displayData.sort((a, b) => {
      if (formData.searchBy === "datewise") return parseReturnDate(a.key) - parseReturnDate(b.key);
      return a.key.localeCompare(b.key);
    });
  } else if (formData.searchBy === "productwise" && formData.reportType === "abstract") {
    const groupedData = filteredPurchaseReturnData.reduce((acc, item) => {
      const key = item.itemDescription;
      if (!acc[key]) {
        acc[key] = {
          itemDescription: item.itemDescription,
          batch: item.batch || '',
          qty: 0,
          value: 0,
          discAmt: 0,
          cgstAmt: 0,
          sgstAmt: 0,
          netValue: 0
        };
      }
      acc[key].qty += parseFloat(item.returnQty) || 0;
      acc[key].value += parseFloat(item.value) || 0;
      acc[key].discAmt += parseFloat(item.disc) || 0;
      acc[key].cgstAmt += parseFloat(item.camt) || 0;
      acc[key].sgstAmt += parseFloat(item.samt) || 0;
      acc[key].netValue += parseFloat(item.valueWithTax) || 0;
      return acc;
    }, {});
    displayData = Object.values(groupedData);
    displayData.sort((a, b) => a.itemDescription.localeCompare(b.itemDescription));
  } else {
    // For detail reports and other cases
    displayData = [...filteredPurchaseReturnData];
    displayData.sort((a, b) => {
      if (formData.searchBy === "supplierwise") {
        return a.supplier.localeCompare(b.supplier) || parseReturnDate(a.returnDate) - parseReturnDate(b.returnDate);
      }
      if (formData.searchBy === "groupwise") {
        return a.groupName.localeCompare(b.groupName) || parseReturnDate(a.returnDate) - parseReturnDate(b.returnDate);
      }
      if (formData.searchBy === "productwise") {
        return a.itemDescription.localeCompare(b.itemDescription) || parseReturnDate(a.returnDate) - parseReturnDate(b.returnDate);
      }
      if (formData.searchBy === "returnnowise") {
        return a.returnNo.localeCompare(b.returnNo);
      }
      if (formData.searchBy === "invoicewise") {
        return a.invoiceNo.localeCompare(b.invoiceNo) || parseReturnDate(a.returnDate) - parseReturnDate(b.returnDate);
      }
      return parseReturnDate(a.returnDate) - parseReturnDate(b.returnDate);
    });
  }

  // Ensure displayData is always an array
  displayData = Array.isArray(displayData) ? displayData : [];

  const city = branchProfile.city || getCityFromAddress(branchProfile.address);

  // Calculate totals
  const totalQty = displayData.reduce((sum, item) => sum + (parseFloat(item.qty) || parseFloat(item.returnQty) || 0), 0);
  const totalValue = displayData.reduce((sum, item) => sum + (parseFloat(item.value) || 0), 0);
  const totalDiscAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.discAmt) || parseFloat(item.disc) || 0), 0);
  const totalCgstAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.cgstAmt) || parseFloat(item.camt) || 0), 0);
  const totalSgstAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.sgstAmt) || parseFloat(item.samt) || 0), 0);
  const totalGstAmt = totalCgstAmt + totalSgstAmt;
  const totalCessAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.cessAmt) || 0), 0);
  const totalNetValue = displayData.reduce((sum, item) => sum + (parseFloat(item.netValue) || parseFloat(item.valueWithTax) || 0), 0);

  const renderTableHeaders = () => {
    if (formData.searchBy === "datewise" && formData.reportType === "detail") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>MRR.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>MRR.Dt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Supplier</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Product Name</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Qty</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Rate</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Gross Amt.</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc.Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>SGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    } else if (formData.searchBy === "supplierwise" && formData.reportType === "detail") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>MRR.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>MRR.Dt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Gross Amt.</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>SGST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amnt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Others</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    } else if ((formData.searchBy === "datewise" || formData.searchBy === "supplierwise" || formData.searchBy === "groupwise") && formData.reportType === "abstract") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            {formData.searchBy === "datewise" ? "Date" : formData.searchBy === "supplierwise" ? "Supplier" : "Group Name"}
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>From-To (MRR No's)</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Qty</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Value</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    } else if (formData.searchBy === "productwise" && formData.reportType === "abstract") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Product Name</th>
   
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Qty</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Value</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    } else if ((formData.searchBy === "returnnowise" || formData.searchBy === "invoicewise") && formData.reportType === "detail") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            {formData.searchBy === "returnnowise" ? "Return No" : "Invoice No"}
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Return Date</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Supplier</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Product Name</th>

          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Qty</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Value</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CGST Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>SGST Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    } else {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>S.No</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Product Name</th>
       
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Qty</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Value</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Disc Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>CGST Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>SGST Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>GST Amt</th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>Net Value</th>
        </tr>
      );
    }
  };

  const renderTableRows = () => {
    if (displayData.length === 0) {
      const colSpan = formData.searchBy === "datewise" && formData.reportType === "detail" ? 13 :
                     formData.searchBy === "supplierwise" && formData.reportType === "detail" ? 10 :
                     (formData.searchBy === "datewise" || formData.searchBy === "supplierwise" || formData.searchBy === "groupwise") && formData.reportType === "abstract" ? 8 :
                     formData.searchBy === "productwise" && formData.reportType === "abstract" ? 8 :
                     (formData.searchBy === "returnnowise" || formData.searchBy === "invoicewise") && formData.reportType === "detail" ? 14 : 11;
  
      return (
        <tr>
          <td colSpan={colSpan} style={{ 
            border: '1px solid #cbd5e1', 
            padding: '30px', 
            textAlign: 'center', 
            color: '#64748b', 
            fontStyle: 'italic',
            fontSize: '14px',
            backgroundColor: '#f8fafc'
          }}>
            No purchase return data found for the selected criteria
          </td>
        </tr>
      );
    }
  
    return displayData.map((item, index) => {
      if (formData.searchBy === "datewise" && formData.reportType === "detail") {
        return (
          <tr key={`${item.returnId}-${item.itemDescription}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.returnNo || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{formatDisplayDate(item.returnDate)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.supplier}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.itemDescription}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.returnQty) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.rate) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.value) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.disc) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.camt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.samt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{((parseFloat(item.camt) || 0) + (parseFloat(item.samt) || 0)).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.valueWithTax) || 0).toFixed(2)}</td>
          </tr>
        );
      }else if (formData.searchBy === "supplierwise" && formData.reportType === "detail") {
        return (
          <tr key={`${item.returnId}-${item.itemDescription}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{item.returnNo || 'N/A'}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{formatDisplayDate(item.returnDate)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.value) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.disc) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.camt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.samt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{((parseFloat(item.camt) || 0) + (parseFloat(item.samt) || 0)).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.cessAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.valueWithTax) || 0).toFixed(2)}</td>
          </tr>
        );
      } else if ((formData.searchBy === "datewise" || formData.searchBy === "supplierwise" || formData.searchBy === "groupwise") && formData.reportType === "abstract") {
        return (
          <tr key={`${item.key}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {formData.searchBy === "datewise" ? formatDisplayDate(item.key) : item.key}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {item.returnNos && item.returnNos.length > 0 ? `${item.returnNos[0]} - ${item.returnNos[item.returnNos.length - 1]}` : 'N/A'}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.qty) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.value) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.discAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{((parseFloat(item.cgstAmt) || 0) + (parseFloat(item.sgstAmt) || 0)).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.netValue) || 0).toFixed(2)}</td>
          </tr>
        );
      } else if (formData.searchBy === "productwise" && formData.reportType === "abstract") {
        return (
          <tr key={`${item.itemDescription}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.itemDescription}</td>

            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.qty) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.value) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.discAmt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{((parseFloat(item.cgstAmt) || 0) + (parseFloat(item.sgstAmt) || 0)).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.netValue) || 0).toFixed(2)}</td>
          </tr>
        );
      } else if ((formData.searchBy === "returnnowise" || formData.searchBy === "invoicewise") && formData.reportType === "detail") {
        return (
          <tr key={`${item.returnId}-${item.itemDescription}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {formData.searchBy === "returnnowise" ? item.returnNo : item.invoiceNo || 'N/A'}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{formatDisplayDate(item.returnDate)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.supplier}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.itemDescription}</td>

            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.returnQty) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.value) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.disc) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.camt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.samt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{((parseFloat(item.camt) || 0) + (parseFloat(item.samt) || 0)).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.valueWithTax) || 0).toFixed(2)}</td>
          </tr>
        );
      } else {
        return (
          <tr key={`${item.returnId}-${item.itemDescription}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>{index + 1}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>{item.itemDescription}</td>
      
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>{(parseFloat(item.returnQty) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.value) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.disc) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.camt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.samt) || 0).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{((parseFloat(item.camt) || 0) + (parseFloat(item.samt) || 0)).toFixed(2)}</td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>₹{(parseFloat(item.valueWithTax) || 0).toFixed(2)}</td>
          </tr>
        );
      }
    });
  };

  const renderTotalRow = () => {
    if (displayData.length === 0) return null;

    if (formData.searchBy === "datewise" && formData.reportType === "detail") {
      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="6" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Grand Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>-</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalDiscAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalCgstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalSgstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalGstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalNetValue.toFixed(2)}</td>
        </tr>
      );
    } else if (formData.searchBy === "supplierwise" && formData.reportType === "detail") {
      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="3" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Grand Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalDiscAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalCgstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalSgstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalGstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalCessAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalNetValue.toFixed(2)}</td>
        </tr>
      );
    } else if ((formData.searchBy === "datewise" || formData.searchBy === "supplierwise" || formData.searchBy === "groupwise") && formData.reportType === "abstract") {
      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="3" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Grand Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalQty.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalDiscAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalGstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalNetValue.toFixed(2)}</td>
        </tr>
      );
    } else if (formData.searchBy === "productwise" && formData.reportType === "abstract") {
      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="2" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Grand Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalQty.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalDiscAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalGstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalNetValue.toFixed(2)}</td>
        </tr>
      );
    } else if ((formData.searchBy === "returnnowise" || formData.searchBy === "invoicewise") && formData.reportType === "detail") {
      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="5" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Grand Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalQty.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalDiscAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalCgstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalSgstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalGstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalNetValue.toFixed(2)}</td>
        </tr>
      );
    } else {
      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="4" style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '600' }}>
            Grand Total
          </td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>{totalQty.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalValue.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalDiscAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalCgstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalSgstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalGstAmt.toFixed(2)}</td>
          <td style={{ border: '1px solid #cbd5e1', padding: '10px', textAlign: 'right', fontSize: '12px', fontWeight: '600' }}>₹{totalNetValue.toFixed(2)}</td>
        </tr>
      );
    }
  };

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '30px',
      marginTop: '20px',
      border: '2px solid #3b82f6',
      borderRadius: '12px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      pageBreakInside: 'avoid',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    }} className="print-only">
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            margin: '0', 
            marginBottom: '15px',
            color: '#1e293b',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
          }}>
            {branchProfile.branchName}
          </h1>
          <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#475569' }}>
            <div style={{ marginBottom: '8px', fontSize: '16px', fontWeight: '600', color: '#3b82f6' }}>
              {formData.searchBy.charAt(0).toUpperCase() + formData.searchBy.slice(1)} Purchase Return Report
            </div>
            <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
              <span><strong>From:</strong> {new Date(formData.fromDate).toLocaleDateString('en-GB')}</span>
              <span><strong>To:</strong> {new Date(formData.toDate).toLocaleDateString('en-GB')}</span>
            </div>
          </div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '15px',
          textAlign: 'center',
          minWidth: '150px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{city}</div>
          <div style={{ fontSize: '12px', marginTop: '5px', opacity: '0.9' }}>
            Option: {formData.reportType === 'detail' ? 'Detail' : 'Abstract'}
          </div>
        </div>
      </div>

      {/* Purchase Return Table */}
      <div style={{ overflowX: 'auto' }}>
       <table style={{ 
  width: '100%', 
  borderCollapse: 'collapse', 
  fontSize: '11px', 
  border: '2px solid #3b82f6',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
}} className={
  formData.searchBy === "datewise" && formData.reportType === "detail" ? "datewise-detail" :
  formData.searchBy === "supplierwise" && formData.reportType === "detail" ? "supplierwise-detail" :
  (formData.searchBy === "datewise" || formData.searchBy === "supplierwise" || formData.searchBy === "groupwise") && formData.reportType === "abstract" ? "abstract" :
  formData.searchBy === "productwise" && formData.reportType === "abstract" ? "productwise-abstract" :
  (formData.searchBy === "returnnowise" || formData.searchBy === "invoicewise") && formData.reportType === "detail" ? "return-invoice-detail" :
  "default"
}>
          <thead>
            {renderTableHeaders()}
          </thead>
          <tbody>
            {renderTableRows()}
            {renderTotalRow()}
          </tbody>
        </table>
      </div>

      {/* Summary Section */}
      {displayData.length > 0 && (
        <div style={{ 
          marginTop: '30px', 
          padding: '20px', 
          backgroundColor: '#f8fafc', 
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '15px',
            color: '#1e293b'
          }}>Summary</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: '15px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Records</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>{displayData.length}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Quantity</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>{totalQty.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Value</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>₹{totalValue.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Net Value</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>₹{totalNetValue.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Purchase Return Report Generator Component
const PurchaseReturnReportGenerator = () => {
  const today = new Date();
  const [fromDate, setFromDate] = useState(today.toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(today.toISOString().split('T')[0]);
  const [searchBy, setSearchBy] = useState("datewise");
  const [reportType, setReportType] = useState("detail");
  const [filters, setFilters] = useState({
    supplier: ["All Supplier Names"],
    group: ["All Group Names"],
    product: ["All Product Names"]
  });
  const [suppliers, setSuppliers] = useState(["All Supplier Names"]);
  const [groups, setGroups] = useState(["All Group Names"]);
  const [products, setProducts] = useState(["All Product Names"]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: ""
  });
  const [purchaseReturnData, setPurchaseReturnData] = useState([]);
  const [productGroupMap, setProductGroupMap] = useState({});
  const [groupsMap, setGroupsMap] = useState({});

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  // Get available report types based on search criteria
  const getAvailableReportTypes = () => {
    switch (searchBy) {
      case "datewise":
      case "supplierwise":
        return [
          { value: "detail", label: "Detail" },
          { value: "abstract", label: "Abstract" }
        ];
      case "groupwise":
      case "productwise":
        return [
          { value: "abstract", label: "Abstract" }
        ];
      case "returnnowise":
      case "invoicewise":
        return [
          { value: "detail", label: "Detail" }
        ];
      default:
        return [
          { value: "detail", label: "Detail" },
          { value: "abstract", label: "Abstract" }
        ];
    }
  };

  // Update report type when search criteria changes
  useEffect(() => {
    const availableTypes = getAvailableReportTypes();
    if (!availableTypes.some(type => type.value === reportType)) {
      setReportType(availableTypes[0].value);
    }
  }, [searchBy, reportType]);

  // Firebase Auth and Data Loading
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        loadFirebaseData(user);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

// Replace the existing loadFirebaseData function with this corrected version
const loadFirebaseData = (user) => {
  const encodedEmail = encodeEmailForPath(user.email);

  // Load Branch Profile (unchanged)
  const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
  onValue(branchProfileRef, (snapshot) => {
    const data = snapshot.val();
    if (data) {
      const profiles = Object.values(data);
      const latestProfile = profiles[profiles.length - 1];
      setBranchProfile({
        branchName: latestProfile.branchName || "SUPREME TRADERS",
        address: `${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`.trim(),
        phone: latestProfile.mobileNo || '',
        city: latestProfile.city || getCityFromAddress(`${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`)
      });
    }
  });

  // Load Suppliers (unchanged)
  const suppliersRef = ref(database, `users/${encodedEmail}/suppliers`);
  onValue(suppliersRef, (snapshot) => {
    const suppliersData = ["All Supplier Names"];
    if (snapshot.exists()) {
      snapshot.forEach((childSnapshot) => {
        const supplier = childSnapshot.val();
        if (supplier.supplierName && !suppliersData.includes(supplier.supplierName)) {
          suppliersData.push(supplier.supplierName);
        }
      });
    }
    setSuppliers(suppliersData);
  });

  // Load Groups (unchanged)
  const groupsRef = ref(database, `users/${encodedEmail}/productGroups`);
  onValue(groupsRef, (snapshot) => {
    const groupsData = ["All Group Names"];
    const groupsMapping = {};
    if (snapshot.exists()) {
      snapshot.forEach((childSnapshot) => {
        const group = childSnapshot.val();
        if (group.name && !groupsData.includes(group.name)) {
          groupsData.push(group.name);
          groupsMapping[childSnapshot.key] = group.name;
        }
      });
    }
    setGroups(groupsData);
    setGroupsMap(groupsMapping);
  });

  // Load Products (unchanged)
  const productsRef = ref(database, `users/${encodedEmail}/products`);
  onValue(productsRef, (snapshot) => {
    const productsData = ["All Product Names"];
    const groupMap = {};
    if (snapshot.exists()) {
      snapshot.forEach((childSnapshot) => {
        const product = childSnapshot.val();
        if (product.productName && !productsData.includes(product.productName)) {
          productsData.push(product.productName);
          let groupName = '';
          if (product.groupName) {
            groupName = product.groupName;
          } else if (product.group && groupsMap[product.group]) {
            groupName = groupsMap[product.group];
          } else if (product.group) {
            groupName = product.group;
          } else {
            groupName = '';
          }
          groupMap[product.productName] = groupName;
        }
      });
    }
    setProducts(productsData);
    setProductGroupMap(groupMap);
  });

  return () => {
    off(branchProfileRef);
    off(suppliersRef);
    off(groupsRef);
    off(productsRef);
  };
};

// Add this new function to load purchase return data based on date range
const loadPurchaseReturnData = (user, fromDate, toDate) => {
  const encodedEmail = encodeEmailForPath(user.email);
  const returnsRef = ref(database, `users/${encodedEmail}/purchaseReturns`);
  
  onValue(returnsRef, (snapshot) => {
    const data = snapshot.val();
    if (data) {
      const returnRecords = [];
      const startDate = new Date(fromDate);
      const endDate = new Date(toDate);
      endDate.setHours(23, 59, 59, 999);
      
      Object.entries(data).forEach(([returnId, returnData]) => {
        if (returnData.items && Array.isArray(returnData.items) && returnData.formData) {
          const returnDate = parseReturnDate(returnData.formData.returnDate);
          
          if (returnDate && !isNaN(returnDate.getTime()) && 
              returnDate >= startDate && returnDate <= endDate) {
            
            returnData.items.forEach(item => {
              if (item.itemDescription) {
                // Get return quantity
                const returnQty = parseFloat(item.returnQty) || 0;
                
                // Get the rate from the item (this should be the purchase rate)
                const rate = parseFloat(item.value) || 0; // value field contains the rate
                
                // Calculate gross amount: rate * return quantity
                const grossAmount = rate * returnQty;
                
                // Get discount percentage and calculate discount amount
                const discountPercent = parseFloat(item.disc) || 0;
                const discountAmt = (grossAmount * discountPercent) / 100;
                
                // Get tax percentages and calculate tax amounts
                const cgstPercent = parseFloat(item.camt) || 0;
                const sgstPercent = parseFloat(item.samt) || 0;
                const cessAmt = parseFloat(item.cessAmt) || 0;
                
                // Calculate taxable amount (gross - discount)
                const taxableAmount = grossAmount - discountAmt;
                
                // Calculate tax amounts
                const cgstAmt = (taxableAmount * cgstPercent) / 100;
                const sgstAmt = (taxableAmount * sgstPercent) / 100;
                
                // Calculate net value: gross amount - discount + taxes + cess
                const netValue = grossAmount - discountAmt + cgstAmt + sgstAmt + cessAmt;
                
                returnRecords.push({
                  returnId: returnId,
                  returnNo: returnData.formData.returnNo || 'N/A',
                  returnDate: returnData.formData.returnDate || "",
                  supplier: returnData.formData.supplier || "Unknown Supplier",
                  invoiceNo: returnData.formData.invoiceNo || 'N/A',
                  invoiceDate: returnData.formData.invoiceDate || '',
                  itemDescription: item.itemDescription || "Unknown Product",
                  groupName: productGroupMap[item.itemDescription] || 'No Group Assigned',
                  batch: item.batch || "",
                  expiryDate: item.expiryDate || "",
                  returnQty: returnQty,
                  rate: rate, // This is the purchase rate per unit
                  value: grossAmount, // This is the gross amount (rate * returnQty)
                  disc: discountAmt, // Discount amount (not percentage)
                  camt: cgstAmt, // CGST amount (not percentage)
                  samt: sgstAmt, // SGST amount (not percentage)
                  cessAmt: cessAmt,
                  valueWithTax: netValue // Net value after discount and taxes
                });
              }
            });
          }
        }
      });
      setPurchaseReturnData(returnRecords);
    } else {
      setPurchaseReturnData([]);
    }
  });
};

// Add this useEffect to reload data when date range changes
useEffect(() => {
  if (currentUser && fromDate && toDate) {
    loadPurchaseReturnData(currentUser, fromDate, toDate);
  }
}, [currentUser, fromDate, toDate, productGroupMap]);

// Also add this helper function at the top of your component (if not already present)
const parseReturnDate = (dateString) => {
  if (!dateString) return null;
  // Handle DD-MM-YYYY or DD/MM/YYYY
  if (/^\d{2}[-\/]\d{2}[-\/]\d{4}$/.test(dateString)) {
    const parts = dateString.split(/[-\/]/);
    return new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
  }
  // Handle YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const parts = dateString.split('-');
    return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
  }
  // Fallback to default Date parsing
  return new Date(dateString);
};

  // Update purchase return data when productGroupMap or groupsMap changes
  useEffect(() => {
    if (Object.keys(productGroupMap).length > 0 && currentUser) {
      setPurchaseReturnData(prevData =>
        prevData.map(item => ({
          ...item,
          groupName: productGroupMap[item.itemDescription] || '',
        }))
      );
    }
  }, [productGroupMap, groupsMap, currentUser]);

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleGenerateReport = () => {
    setShowReport(true);
  };

 const handlePrint = () => {
  const printContent = (
    <PurchaseReturnReportDisplay 
      formData={{ fromDate, toDate, searchBy, reportType }}
      filters={filters}
      branchProfile={branchProfile}
      purchaseReturnData={purchaseReturnData}
    />
  );

  const htmlString = ReactDOMServer.renderToString(printContent);

  const printWindow = window.open('', '', 'width=1200,height=800');
  printWindow.document.write(`
    <html>
      <head>
        <title>Purchase Return Report</title>
        <style>
          body { 
            margin: 0; 
            padding: 5px; 
            font-family: Arial, sans-serif; 
            font-size: 8px;
          }
          @page { 
            size: A4 landscape; 
            margin: 5mm; 
          }
          table { 
            page-break-inside: auto;
            width: 100%;
            font-size: 7px;
            table-layout: fixed;
            border-collapse: collapse;
          }
          tr { 
            page-break-inside: avoid; 
            page-break-after: auto; 
          }
          th, td {
                padding: 4px !important;
            font-size: 12px !important;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 60px;
          }
          
          /* Datewise detail columns */
          .datewise-detail th:nth-child(1), .datewise-detail td:nth-child(1) { width: 30px; } /* S.No */
          .datewise-detail th:nth-child(2), .datewise-detail td:nth-child(2) { width: 50px; } /* MRR No */
          .datewise-detail th:nth-child(3), .datewise-detail td:nth-child(3) { width: 50px; } /* MRR Date */
          .datewise-detail th:nth-child(4), .datewise-detail td:nth-child(4) { width: 80px; } /* Supplier */
          .datewise-detail th:nth-child(5), .datewise-detail td:nth-child(5) { width: 80px; } /* Product */
          .datewise-detail th:nth-child(6), .datewise-detail td:nth-child(6) { width: 40px; } /* Qty */
          .datewise-detail th:nth-child(7), .datewise-detail td:nth-child(7) { width: 40px; } /* Rate */
          .datewise-detail th:nth-child(8), .datewise-detail td:nth-child(8) { width: 50px; } /* Gross */
          .datewise-detail th:nth-child(9), .datewise-detail td:nth-child(9) { width: 45px; } /* Disc */
          .datewise-detail th:nth-child(10), .datewise-detail td:nth-child(10) { width: 50px; } /* CGST */
          .datewise-detail th:nth-child(11), .datewise-detail td:nth-child(11) { width: 50px; } /* SGST */
          .datewise-detail th:nth-child(12), .datewise-detail td:nth-child(12) { width: 50px; } /* GST */
          .datewise-detail th:nth-child(13), .datewise-detail td:nth-child(13) { width: 60px; } /* Net Value */

          /* Supplierwise detail columns */
          .supplierwise-detail th:nth-child(1), .supplierwise-detail td:nth-child(1) { width: 30px; } /* S.No */
          .supplierwise-detail th:nth-child(2), .supplierwise-detail td:nth-child(2) { width: 60px; } /* MRR No */
          .supplierwise-detail th:nth-child(3), .supplierwise-detail td:nth-child(3) { width: 50px; } /* MRR Date */
          .supplierwise-detail th:nth-child(4), .supplierwise-detail td:nth-child(4) { width: 60px; } /* Gross */
          .supplierwise-detail th:nth-child(5), .supplierwise-detail td:nth-child(5) { width: 50px; } /* Disc */
          .supplierwise-detail th:nth-child(6), .supplierwise-detail td:nth-child(6) { width: 50px; } /* CGST */
          .supplierwise-detail th:nth-child(7), .supplierwise-detail td:nth-child(7) { width: 50px; } /* SGST */
          .supplierwise-detail th:nth-child(8), .supplierwise-detail td:nth-child(8) { width: 50px; } /* GST */
          .supplierwise-detail th:nth-child(9), .supplierwise-detail td:nth-child(9) { width: 45px; } /* Others */
          .supplierwise-detail th:nth-child(10), .supplierwise-detail td:nth-child(10) { width: 60px; } /* Net Value */

          /* Abstract report columns */
          .abstract th:nth-child(1), .abstract td:nth-child(1) { width: 30px; } /* S.No */
          .abstract th:nth-child(2), .abstract td:nth-child(2) { width: 100px; } /* Key column */
          .abstract th:nth-child(3), .abstract td:nth-child(3) { width: 80px; } /* MRR Nos */
          .abstract th:nth-child(4), .abstract td:nth-child(4) { width: 40px; } /* Qty */
          .abstract th:nth-child(5), .abstract td:nth-child(5) { width: 50px; } /* Value */
          .abstract th:nth-child(6), .abstract td:nth-child(6) { width: 45px; } /* Disc */
          .abstract th:nth-child(7), .abstract td:nth-child(7) { width: 50px; } /* GST */
          .abstract th:nth-child(8), .abstract td:nth-child(8) { width: 60px; } /* Net Value */

          /* Productwise abstract columns */
          .productwise-abstract th:nth-child(1), .productwise-abstract td:nth-child(1) { width: 30px; } /* S.No */
          .productwise-abstract th:nth-child(2), .productwise-abstract td:nth-child(2) { width: 120px; } /* Product */
          .productwise-abstract th:nth-child(3), .productwise-abstract td:nth-child(3) { width: 40px; } /* Qty */
          .productwise-abstract th:nth-child(4), .productwise-abstract td:nth-child(4) { width: 50px; } /* Value */
          .productwise-abstract th:nth-child(5), .productwise-abstract td:nth-child(5) { width: 45px; } /* Disc */
          .productwise-abstract th:nth-child(6), .productwise-abstract td:nth-child(6) { width: 50px; } /* GST */
          .productwise-abstract th:nth-child(7), .productwise-abstract td:nth-child(7) { width: 60px; } /* Net Value */

          /* Return/Invoice wise detail columns */
          .return-invoice-detail th:nth-child(1), .return-invoice-detail td:nth-child(1) { width: 30px; } /* S.No */
          .return-invoice-detail th:nth-child(2), .return-invoice-detail td:nth-child(2) { width: 60px; } /* Return/Invoice No */
          .return-invoice-detail th:nth-child(3), .return-invoice-detail td:nth-child(3) { width: 50px; } /* Date */
          .return-invoice-detail th:nth-child(4), .return-invoice-detail td:nth-child(4) { width: 70px; } /* Supplier */
          .return-invoice-detail th:nth-child(5), .return-invoice-detail td:nth-child(5) { width: 80px; } /* Product */
          .return-invoice-detail th:nth-child(6), .return-invoice-detail td:nth-child(6) { width: 40px; } /* Qty */
          .return-invoice-detail th:nth-child(7), .return-invoice-detail td:nth-child(7) { width: 50px; } /* Value */
          .return-invoice-detail th:nth-child(8), .return-invoice-detail td:nth-child(8) { width: 45px; } /* Disc */
          .return-invoice-detail th:nth-child(9), .return-invoice-detail td:nth-child(9) { width: 50px; } /* CGST */
          .return-invoice-detail th:nth-child(10), .return-invoice-detail td:nth-child(10) { width: 50px; } /* SGST */
          .return-invoice-detail th:nth-child(11), .return-invoice-detail td:nth-child(11) { width: 50px; } /* GST */
          .return-invoice-detail th:nth-child(12), .return-invoice-detail td:nth-child(12) { width: 60px; } /* Net Value */

          /* Default columns */
          .default th:nth-child(1), .default td:nth-child(1) { width: 30px; } /* S.No */
          .default th:nth-child(2), .default td:nth-child(2) { width: 100px; } /* Product */
          .default th:nth-child(3), .default td:nth-child(3) { width: 40px; } /* Qty */
          .default th:nth-child(4), .default td:nth-child(4) { width: 50px; } /* Value */
          .default th:nth-child(5), .default td:nth-child(5) { width: 45px; } /* Disc */
          .default th:nth-child(6), .default td:nth-child(6) { width: 50px; } /* CGST */
          .default th:nth-child(7), .default td:nth-child(7) { width: 50px; } /* SGST */
          .default th:nth-child(8), .default td:nth-child(8) { width: 50px; } /* GST */
          .default th:nth-child(9), .default td:nth-child(9) { width: 60px; } /* Net Value */
          
          .print-only {
            width: 93% !important;
            max-width: none !important;
            overflow: visible !important;
          }
          div[style*="overflowX"] {
            overflow: visible !important;
          }
        </style>
      </head>
      <body>
        <div id="print-content">${htmlString}</div>
        <script>
          setTimeout(function() {
            window.print();
            window.close();
          }, 1000);
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
};

  const formData = {
    fromDate,
    toDate,
    searchBy,
    reportType
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '18px',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <div>Loading Purchase Return Data...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '3rem', 
        color: '#64748b', 
        fontSize: '18px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <h2 style={{ color: '#1e293b', marginBottom: '16px' }}>Authentication Required</h2>
        <p>Please login to access the Purchase Return Report </p>
      </div>
    );
  }

  const availableReportTypes = getAvailableReportTypes();

  return (
    <div style={{
      width: '100%',
      maxWidth: '90rem',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      padding: '32px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      border: '1px solid #e2e8f0'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media print {
            body * {
              visibility: hidden;
            }
            .print-only, .print-only * {
              visibility: visible;
            }
            .print-only {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0;
              padding: 20px;
              box-shadow: none;
              border: none;
            }
          }
        `}
      </style>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{
          width: '6px',
          height: '32px',
          backgroundColor: '#3b82f6',
          borderRadius: '3px'
        }}></div>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1e293b',
          margin: '0'
        }}>Purchase Return Report </h1>
      </div>
      
      {/* Date Range and Search Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Search By</label>
          <select
            value={searchBy}
            onChange={(e) => setSearchBy(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            <option value="datewise">Datewise</option>
            <option value="supplierwise">Supplierwise</option>
            <option value="groupwise">Groupwise</option>
            <option value="productwise">Productwise</option>
            <option value="returnnowise">Return No wise</option>
            <option value="invoicewise">Invoicewise</option>
          </select>
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Report Type</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            {availableReportTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Filter Checkboxes */}
     <div style={{
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '0px',
  marginBottom: '32px'
}}>
  <FilterSection 
    title="Supplier Name" 
    options={suppliers} 
    selected={filters.supplier} 
    onChange={(value) => handleFilterChange("supplier", value)}
  />
  <FilterSection 
    title="Group Name" 
    options={groups} 
    selected={filters.group} 
    onChange={(value) => handleFilterChange("group", value)}
  />
  <FilterSection 
    title="Product Name" 
    options={products} 
    selected={filters.product} 
    onChange={(value) => handleFilterChange("product", value)}
  />
</div>

      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '32px',
        justifyContent: 'center'
      }}>
        <button
          onClick={handleGenerateReport}
          style={{
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '140px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Generate Report
        </button>
        <button
          onClick={handlePrint}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Print Report
        </button>
      </div>

      {/* Purchase Return Report Display */}
      {showReport && (
        <PurchaseReturnReportDisplay 
          formData={formData}
          filters={filters}
          branchProfile={branchProfile}
          purchaseReturnData={purchaseReturnData}
        />
      )}
    </div>
  );
};

export default PurchaseReturnReportGenerator;